import React, { useEffect, useMemo, useState } from 'react';
import { DiscussionPanel } from '../../modules/discussion';

export interface SessionHubParticipant {
  userId: number;
  displayName: string;
  role: string;
  hasJoined: boolean;
  joinedAt?: string | null;
}

export interface SessionHubData {
  id: number;
  scheduleId: number;
  organizationId?: number;
  classroomId?: number | null;
  classroomName: string;
  courseName?: string | null;
  teacherUserId: number;
  teacherDisplayName: string;
  studentUserId?: number | null;
  studentDisplayName?: string | null;
  targetType: string;
  title: string;
  locationType: string;
  startDateTime: string;
  endDateTime: string;
  actualStartDateTime?: string | null;
  actualEndDateTime?: string | null;
  status: string;
  location?: string | null;
  meetingUrl?: string | null;
  notes?: string | null;
  currentUserJoined: boolean;
  currentUserJoinedAt?: string | null;
  participants: SessionHubParticipant[];
}

interface SessionHubProps {
  session: SessionHubData;
  currentUserName: string;
  currentUserKey?: string;
  getDiscussionHeaders: () => Promise<Record<string, string>>;
  getDiscussionAccessToken: () => Promise<string>;
  useApiDiscussion?: boolean;
  useLiveDiscussion?: boolean;
  canManageSession?: boolean;
  allowJoinSession?: boolean;
  showMeetingLink?: boolean;
  joining?: boolean;
  saving?: boolean;
  joinMessage?: string | null;
  onJoin?: () => Promise<void> | void;
  onStartSession?: () => Promise<void> | void;
  onCompleteSession?: () => Promise<void> | void;
}

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatJoinedTime = (value?: string | null): string => {
  if (!value) {
    return 'Not joined yet';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return `Joined ${date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
};

const getMeetingProvider = (meetingUrl?: string | null): string => {
  if (!meetingUrl) {
    return 'In person / manual';
  }

  const value = meetingUrl.toLowerCase();
  if (value.includes('teams')) {
    return 'Microsoft Teams';
  }
  if (value.includes('zoom')) {
    return 'Zoom';
  }
  if (value.includes('meet.google')) {
    return 'Google Meet';
  }

  return 'Online meeting';
};

const getStatusPresentation = (status: string) => {
  if (status === 'InProgress') {
    return { label: 'Live', color: '#166534', background: '#dcfce7', border: '#86efac' };
  }

  if (status === 'Completed') {
    return { label: 'Completed', color: '#1d4ed8', background: '#dbeafe', border: '#93c5fd' };
  }

  if (status === 'Cancelled') {
    return { label: 'Cancelled', color: '#b91c1c', background: '#ffe4e6', border: '#fda4af' };
  }

  return { label: 'Upcoming', color: '#475569', background: '#f8fafc', border: '#cbd5e1' };
};

const SessionHub: React.FC<SessionHubProps> = ({
  session,
  currentUserName,
  currentUserKey,
  getDiscussionHeaders,
  getDiscussionAccessToken,
  useApiDiscussion = true,
  useLiveDiscussion = true,
  canManageSession = false,
  allowJoinSession = true,
  showMeetingLink = true,
  joining = false,
  saving = false,
  joinMessage,
  onJoin,
  onStartSession,
  onCompleteSession,
}) => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const [activeMobileTab, setActiveMobileTab] = useState<'details' | 'discussion' | 'people'>('details');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const status = getStatusPresentation(session.status);
  const joinedCount = useMemo(() => session.participants.filter((item) => item.hasJoined).length, [session.participants]);
  const detailsContent = (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, background: '#fff', padding: 18, display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Session Hub</div>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: 28, lineHeight: 1.15 }}>{session.title || `Session ${session.id}`}</h2>
            <div style={{ color: '#475569', fontSize: 14 }}>
              {session.courseName || session.classroomName}
              {session.courseName && session.classroomName ? ` • ${session.classroomName}` : ''}
            </div>
          </div>
          <span style={{ borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 700, color: status.color, background: status.background, border: `1px solid ${status.border}` }}>
            {status.label}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <HubStat label="Class / Course" value={session.courseName || session.classroomName || '—'} />
          <HubStat label="Date" value={formatDate(session.startDateTime)} />
          <HubStat label="Time" value={`${formatTime(session.startDateTime)} - ${formatTime(session.endDateTime)}`} />
          <HubStat label="Teacher" value={session.teacherDisplayName} />
          <HubStat label="Meeting" value={getMeetingProvider(session.meetingUrl)} />
          <HubStat label="Participants" value={`${session.participants.length} people`} />
        </div>
      </div>

      <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, background: '#fff', padding: 18, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#173159' }}>Join and session actions</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
              {session.currentUserJoined ? formatJoinedTime(session.currentUserJoinedAt) : 'You have not joined this session yet.'}
            </div>
          </div>
          <div style={{ fontSize: 13, color: '#475569' }}>{joinedCount} joined</div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {allowJoinSession && (
            <button
              type="button"
              className="amd-btn amd-btn-primary"
              disabled={joining || session.status === 'Completed'}
              onClick={() => { if (onJoin) { void onJoin(); } }}
              style={{ minWidth: 150 }}
            >
              {joining ? 'Joining...' : 'Join Session'}
            </button>
          )}

          {canManageSession && session.status !== 'InProgress' && session.status !== 'Completed' && (
            <button
              type="button"
              className="amd-btn amd-btn-secondary"
              disabled={saving}
              onClick={() => { if (onStartSession) { void onStartSession(); } }}
            >
              {saving ? 'Processing...' : 'Start Session'}
            </button>
          )}

          {canManageSession && session.status !== 'Completed' && (
            <button
              type="button"
              className="amd-btn amd-btn-secondary"
              disabled={saving}
              onClick={() => { if (onCompleteSession) { void onCompleteSession(); } }}
            >
              {saving ? 'Processing...' : 'Finish Session'}
            </button>
          )}
        </div>

        {joinMessage && (
          <div style={{ borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', padding: '10px 12px', fontSize: 13, border: '1px solid #bfdbfe' }}>
            {joinMessage}
          </div>
        )}

        <div style={{ display: 'grid', gap: 8, color: '#334155', fontSize: 14 }}>
          <div><strong>Session status:</strong> {status.label}</div>
          <div><strong>Meeting provider:</strong> {getMeetingProvider(session.meetingUrl)}</div>
          {showMeetingLink && (
            <div><strong>Meeting link:</strong> {session.meetingUrl ? <a href={session.meetingUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{session.meetingUrl}</a> : 'Not provided'}</div>
          )}
          <div><strong>Location:</strong> {session.location || 'Not specified'}</div>
          {session.notes && <div><strong>Notes:</strong> {session.notes}</div>}
        </div>
      </div>
    </div>
  );

  const peopleContent = (
    <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, background: '#fff', padding: 18, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, color: '#173159' }}>Participants</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{session.participants.length} total</div>
      </div>

      {session.participants.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: 14 }}>No participant list is available for this session yet.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {session.participants.map((participant) => (
            <div key={`${participant.role}-${participant.userId}`} style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#f8fafc', padding: 12, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{participant.displayName}</div>
                <span style={{ fontSize: 11, fontWeight: 700, color: participant.role === 'Teacher' ? '#1d4ed8' : '#475569', background: participant.role === 'Teacher' ? '#dbeafe' : '#e2e8f0', borderRadius: 999, padding: '4px 8px' }}>
                  {participant.role}
                </span>
              </div>
              <div style={{ fontSize: 13, color: participant.hasJoined ? '#166534' : '#64748b', fontWeight: 600 }}>
                {participant.hasJoined ? formatJoinedTime(participant.joinedAt) : 'Not joined yet'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const discussionContent = (
    <DiscussionPanel
      refInfo={{
        entityType: 'Session',
        entityId: session.id,
        organizationId: session.organizationId,
      }}
      currentUserId={String(currentUserKey || 'current-user')}
      currentUserName={currentUserName || 'Current user'}
      getHeaders={getDiscussionHeaders}
      getAccessToken={getDiscussionAccessToken}
      useApi={useApiDiscussion}
      useLive={useLiveDiscussion}
    />
  );

  if (isMobile) {
    const mobileTabs: Array<{ key: 'details' | 'discussion' | 'people'; label: string }> = [
      { key: 'details', label: 'Details' },
      { key: 'discussion', label: 'Discussion' },
      { key: 'people', label: 'People' },
    ];

    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {mobileTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={activeMobileTab === tab.key ? 'amd-btn amd-btn-primary' : 'amd-btn amd-btn-ghost'}
              onClick={() => setActiveMobileTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {activeMobileTab === 'details' && detailsContent}
        {activeMobileTab === 'discussion' && discussionContent}
        {activeMobileTab === 'people' && peopleContent}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.95fr) minmax(320px, 1fr)', gap: 16, alignItems: 'start' }}>
      <div style={{ display: 'grid', gap: 16 }}>
        {detailsContent}
        {peopleContent}
      </div>
      <div style={{ minHeight: 0 }}>
        {discussionContent}
      </div>
    </div>
  );
};

const HubStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#f8fafc', padding: 12 }}>
    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
    <div style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>{value}</div>
  </div>
);

export default SessionHub;